import { useTranslation } from 'react-i18next';
import { ETAPPIO_COMPANY, ETAPPIO_COMPANY_VISITING_ADDRESS } from '../constants/company';
import { contractTitle } from '../utils/contractTitle';
import type { PingDeposit } from '../services/api';
import type { Contract, Project } from '../types';
import { classicSlipAmount, PingClassicSlip } from './PingClassicSlip';

export type PingDocumentContext = {
  project: Pick<Project, 'id' | 'projectNumber' | 'description' | 'customerName' | 'companyName'>;
  contract: Pick<Contract, 'id' | 'projectId' | 'contractNumber' | 'contractName'>;
};

/** Invoice-style payment instructions; the amount is financing, not a new supplier invoice. */
export function PingPaymentDocument({ deposit, context }: { deposit: PingDeposit; context: PingDocumentContext }) {
  const { t, i18n } = useTranslation();
  const { project, contract } = context;
  const money = (ore: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: deposit.currency }).format(ore / 100);

  return <div className="ping-payment-document" data-testid="ping-payment-document">
    <div className="ping-document-content d-flex flex-column">
      <header className="d-flex justify-content-between align-items-start gap-4">
        <div>
          <div className="ping-document-brand">{ETAPPIO_COMPANY.tradeName}</div>
          <p className="mb-0">{t('pingDeposit.document.subtitle')}</p>
        </div>
        <div className="text-end">
          <h1>{t('pingDeposit.document.title')}</h1>
          <span>{t('pingDeposit.document.projectNumber', { number: project.projectNumber })}</span>
        </div>
      </header>
      <div className="ping-document-reference d-flex justify-content-between align-items-center gap-3">
        <div><div className="ping-document-label">{t('pingDeposit.reference')}</div><strong>{deposit.notice!.reference}</strong></div>
        <div className="text-end"><div className="ping-document-label">{t('pingDeposit.amount')}</div><strong data-testid="document-amount">{money(classicSlipAmount(deposit)!)}</strong></div>
      </div>
      {deposit.mode === 'sandbox' && <p className="ping-document-sandbox">{t('pingDeposit.document.sandbox')}</p>}
      <div className="row g-2 ping-document-parties">
        <div className="col-6"><div className="ping-document-label">{t('pingDeposit.document.customer')}</div><strong>{project.customerName}</strong></div>
        <div className="col-6"><div className="ping-document-label">{t('pingDeposit.document.company')}</div><strong>{project.companyName}</strong></div>
      </div>
      <section className="ping-document-project">
        <h2>{t('pingDeposit.document.purpose')}</h2>
        <dl className="mb-0">
          <dt>{t('pingDeposit.document.projectNumber', { number: project.projectNumber })}</dt>
          <dd className="ping-document-description">{project.description}</dd>
          <dt>{t('pingDeposit.document.contractNumber', { number: contract.contractNumber })}</dt>
          <dd className="ping-document-description mb-0">{contractTitle(contract)}</dd>
        </dl>
      </section>
      <dl className="ping-document-totals mb-0">
        <div className="d-flex justify-content-between gap-3"><dt>{t('pingDeposit.document.total')}</dt><dd>{money(deposit.amountOre)}</dd></div>
        {deposit.receivedOre !== null && deposit.receivedOre > 0 && <div className="d-flex justify-content-between gap-3"><dt>{t('pingDeposit.received')}</dt><dd>{money(deposit.receivedOre)}</dd></div>}
        <div className="ping-document-due d-flex justify-content-between gap-3"><dt>{t('pingDeposit.amount')}</dt><dd>{money(classicSlipAmount(deposit)!)}</dd></div>
      </dl>
      <p className="ping-document-instructions">{t('pingDeposit.document.instructions', { bankgiro: deposit.notice!.bankAccount.bankgiro_number })}</p>
      <div className="ping-document-about mt-auto">
        <h2>{t('pingDeposit.document.aboutTitle')}</h2>
        <p>{t('pingDeposit.document.about')}</p>
        <footer className="d-flex justify-content-between gap-3">
          <span>{ETAPPIO_COMPANY.legalName}<br />{t('pingDeposit.document.organizationNumber', { number: ETAPPIO_COMPANY.organizationNumber })}</span>
          <span>{ETAPPIO_COMPANY_VISITING_ADDRESS}<br />{ETAPPIO_COMPANY.email}</span>
        </footer>
      </div>
    </div>
    <div className="ping-document-slip"><PingClassicSlip deposit={deposit} /></div>
  </div>;
}
